// Import the necessary components
import { lazy, type JSX } from 'react';

import { Navigate } from 'react-router-dom';
import type { BreadcrumbComponentType } from 'use-react-router-breadcrumbs';

import type { BreadcrumbsProps } from '../presentation/Breadcrumbs/Breadcrumbs';

import { useIsPinCodeRequired } from '@/access/application/useAccessEnabled';

const AuthScreen = lazy(() => import('../../auth/presentation/AuthScreen/AuthScreen'));
const ForgottenPasswordScreen = lazy(() => import('../../auth/presentation/ForgottenPasswordScreen/ForgottenPasswordScreen'));
const DashboardScreen = lazy(() => import('../../dashboard/presentation/DashboardScreen/DashboardScreen'));
const FleetDetailsScreen = lazy(() => import('../../fleet/presentation/Screens/FleetDetailsScreen/FleetDetailsScreen'));
const FleetPropertiesScreen = lazy(() => import('../../fleet/presentation/Screens/FleetPropertiesScreen/FleetPropertiesScreen').then(m => ({ default: m.FleetPropertiesScreen })));
const FleetsScreen = lazy(() => import('../../fleet/presentation/Screens/FleetsScreen/FleetsScreen').then(m => ({ default: m.FleetsScreen })));
const VehicleDetailsScreen = lazy(() => import('../../fleet/presentation/Screens/VehicleDetailsScreen/VehicleDetailsScreen').then(m => ({ default: m.VehicleDetailsScreen })));
const GroupDetailsScreen = lazy(() => import('../../group/presentation/Screens/GroupDetailsScreen/GroupDetailsScreen'));
const GroupInviteScreen = lazy(() => import('../../group/presentation/Screens/GroupInviteScreen/GroupInviteScreen'));
const GroupPropertiesScreen = lazy(() => import('../../group/presentation/Screens/GroupPropertiesScreen/GroupPropertiesScreen').then(m => ({ default: m.GroupPropertiesScreen })));
const GroupsScreen = lazy(() => import('../../group/presentation/Screens/GroupsScreen/GroupsScreen').then(m => ({ default: m.GroupsScreen })));
const MemberPropertiesScreen = lazy(() => import('../../group/presentation/Screens/MemberPropertiesScreen/MemberPropertiesScreen').then(m => ({ default: m.MemberPropertiesScreen })));

const MyAccessScreen = lazy(() => import('@/access/presentation/Screens/MyAccessScreen/MyAccessScreen'));
const MyAccessDetailScreen = lazy(() => import('@/access/presentation/Screens/MyAccessDetailScreen/MyAccessDetailScreen'));

const AuthAction = lazy(() => import('@/auth/presentation/AuthAction'));
const PolicyScreen = lazy(() => import('@/auth/presentation/PolicyScreen/PolicyScreen'));
const EnterpriseProfileScreen = lazy(() => import('@/dashboard/presentation/EnterpriseProfileScreen.tsx/EnterpriseProfileScreen'));
const ProfileSelectionScreen = lazy(() => import('@/dashboard/presentation/ProfileSelectionScreen/ProfileSelectionScreen'));
const GuestPropertiesScreen = lazy(() => import('@/group/presentation/Screens/GuestPropertiesScreen/GuestPropertiesScreen'));
const HoldingDetailsScreen = lazy(() => import('@/holding/presentation/Screens/HoldingDetailsScreen/HoldingDetailsScreen'));
const HoldingPropertiesScreen = lazy(() => import('@/holding/presentation/Screens/HoldingPropertiesScreen/HoldingPropertiesScreen').then(m => ({ default: m.HoldingPropertiesScreen })));
const HoldingsScreen = lazy(() => import('@/holding/presentation/Screens/HoldingsScreen/HoldingsScreen').then(m => ({ default: m.HoldingsScreen })));
const ParkingMemberScreen = lazy(() => import('@/holding/presentation/Screens/ParkingMemberScreen/ParkingMemberScreen').then(m => ({ default: m.ParkingMemberScreen })));
const ParkingDetailsScreen = lazy(() => import('@/parking/presentation/Screens/ParkingDetailsScreen/ParkingDetailsScreen'));
const ParkingPropertiesScreen = lazy(() => import('@/parking/presentation/Screens/ParkingPropertiesScreen/ParkingPropertiesScreen').then(m => ({ default: m.ParkingPropertiesScreen })));
const ParkingsScreen = lazy(() => import('@/parking/presentation/Screens/ParkingsScreen/ParkingsScreen').then(m => ({ default: m.ParkingsScreen })));
const PlanningScreen = lazy(() => import('@/planning/presentation/PlanningScreen/PlanningScreen').then(m => ({ default: m.PlanningScreen })));
const CompaniesScreen = lazy(() => import('@/subscription/presentation/Screens/CompaniesScreen/CompaniesScreen').then(m => ({ default: m.CompaniesScreen })));
const ParkingCompanyScreen = lazy(() => import('@/subscription/presentation/Screens/ParkingCompanyScreen/ParkingCompanyScreen'));
const PricingClientScreen = lazy(() => import('@/subscription/presentation/Screens/PricingClientScreen/PricingClientScreen'));
const PricingEndUserScreen = lazy(() => import('@/subscription/presentation/Screens/PricingEndUserScreen/PricingEndUserScreen'));
const MFAConfigScreen = lazy(() => import('@/user/presentation/UserProfileScreen/MFAConfigScreen/MFAConfigScreen').then(m => ({ default: m.MFAConfigScreen })));
const UserProfileScreen = lazy(() => import('@/user/presentation/UserProfileScreen/UserProfileScreen/UserProfileScreen').then(m => ({ default: m.UserProfileScreen })));

export interface RouteProp {
    path: string,
    breadcrumb: string | BreadcrumbComponentType,
    element: JSX.Element,
    children?: RouteProp[]
}

// Define the routes
export const routes: RouteProp[] = [


    // Fleets
    { breadcrumb: 'fleets.yourFleets', element: <FleetsScreen />, path: '/fleets' },
    { breadcrumb: '$fleetId', element: <FleetDetailsScreen />, path: '/fleets/:fleetId' },
    { breadcrumb: 'fleet.properties', element: <FleetPropertiesScreen action='edit' />, path: '/fleets/:fleetId/edit' },
    { breadcrumb: 'vehicles.list', element: <FleetDetailsScreen />, path: '/fleets/:fleetId/vehicles' },
    { breadcrumb: '$vehicleId', element: <VehicleDetailsScreen action='view' />, path: '/fleets/:fleetId/vehicles/:vehicleId' },
    { breadcrumb: 'vehicles.edit', element: <VehicleDetailsScreen action='edit' />, path: 'fleets/:fleetId/vehicles/:vehicleId/edit' },

    { breadcrumb: 'vehicles.newVehicle', element: <VehicleDetailsScreen action='add' />, path: '/fleets/:fleetId/vehicles/add-vehicle' },

    { breadcrumb: '', element: <FleetPropertiesScreen action='add' />, path: '/add-fleet' },

    // My access (reservation profiles / pin codes) — breadcrumb resolved per-parking, see useBreadcrumbsRoutes
    { breadcrumb: 'access.allMyPinCode', element: <MyAccessScreen />, path: '/my-access' },
    { breadcrumb: 'access.myAccess', element: <MyAccessDetailScreen action='add' />, path: '/my-access/add' },
    { breadcrumb: 'access.myAccess', element: <MyAccessDetailScreen action='edit' />, path: '/my-access/:profileId' },

    // Groups
    { breadcrumb: 'userGroups.yourUserGroups', element: <GroupsScreen />, path: '/groups' },

    { breadcrumb: '$groupName', element: <GroupDetailsScreen />, path: '/groups/:groupId' },

    { breadcrumb: 'userGroups.groupProperties', element: <GroupPropertiesScreen action='edit' />, path: '/groups/:groupId/edit' },
    { breadcrumb: 'userGroups.details', element: <GroupDetailsScreen />, path: '/groups/:groupId/members' },
    { breadcrumb: 'userGroups.addMember', element: <MemberPropertiesScreen action='add' targetType='userGroup' />, path: '/groups/:groupId/members/add' },
    { breadcrumb: 'userGroups.addMember', element: <MemberPropertiesScreen action='add' defaultTab='import' targetType='userGroup' />, path: '/groups/:groupId/members/import' },
    { breadcrumb: 'userGroups.addMember', element: <MemberPropertiesScreen action='add' targetType='userGroup' defaultTab='select' />, path: '/groups/:groupId/members/select' },
    { breadcrumb: '$guestName', element: <GuestPropertiesScreen />, path: '/groups/:groupId/guests/:guestId' },
    { breadcrumb: 'userGroups.details', element: <GroupDetailsScreen />, path: '/groups/:groupId/guests' },
    { breadcrumb: 'userGroups.addGuest', element: <GuestPropertiesScreen action='add' />, path: '/groups/:groupId/guests/add' },
    { breadcrumb: 'userGroups.memberProperties', element: <MemberPropertiesScreen action='edit' targetType='userGroup' />, path: '/groups/:groupId/members/:memberId' },
    { breadcrumb: 'vehicles.newVehicle', element: <VehicleDetailsScreen action='add' />, path: '/groups/:groupId/fleets/:fleetId/vehicles/add-vehicle' },
    { breadcrumb: 'vehicles.edit', element: <VehicleDetailsScreen action='edit' />, path: '/groups/:groupId/fleets/:fleetId/vehicles/:vehicleId/edit' },
    { breadcrumb: 'userGroups.groupProperties', element: <GroupPropertiesScreen action='add' />, path: '/groups/add' },


    // Groups > Admins
    { breadcrumb: 'userGroups.details', element: <GroupDetailsScreen />, path: '/groups/:groupId/admins' },
    { breadcrumb: 'userGroups.adminsImport', element: <MemberPropertiesScreen defaultTab='add' action='add' targetType='userGroupAdmin' />, path: '/groups/:groupId/admins/add' },
    { breadcrumb: 'userGroups.adminsImport', element: <MemberPropertiesScreen defaultTab='import' action='add' targetType='userGroupAdmin' />, path: '/groups/:groupId/admins/import' },
    { breadcrumb: 'userGroups.usersSelection', element: <MemberPropertiesScreen defaultTab='select' action='add' targetType='userGroupAdmin' />, path: '/groups/:groupId/admins/select' },
    { breadcrumb: 'userGroups.memberProperties', element: <MemberPropertiesScreen action='edit' targetType='userGroupAdmin' />, path: '/groups/:groupId/admins/:memberId' },
    { breadcrumb: 'userGroups.fleetAdmin', element: <MemberPropertiesScreen defaultTab='add' action='add' targetType='fleetAdmin' />, path: '/groups/:groupId/fleets/:fleetId/admins/add' },


    // Parkings
    { breadcrumb: 'parking.parkings', element: <ParkingsScreen />, path: '/parkings' },
    { breadcrumb: 'parking.parking', element: <ParkingDetailsScreen />, path: '/parking' },

    { breadcrumb: '$parkingName', element: <ParkingDetailsScreen />, path: '/parkings/:parkingId' },
    { breadcrumb: '$parkingName', element: <ParkingDetailsScreen />, path: '/parking/:parkingId' },

    { breadcrumb: 'userParkings.addParking', element: <ParkingPropertiesScreen action='add' />, path: '/parkings/add' },
    { breadcrumb: 'userParkings.addParking', element: <ParkingPropertiesScreen action='add' />, path: '/parking/add' },

    { breadcrumb: 'userParkings.parkingEdition', element: <ParkingPropertiesScreen action='edit' />, path: '/parkings/:parkingId/edit' },
    { breadcrumb: 'userParkings.parkingEdition', element: <ParkingPropertiesScreen action='edit' />, path: '/parking/:parkingId/edit' },

    { breadcrumb: 'userParkings.parkingDetails', element: <ParkingDetailsScreen />, path: '/parkings/:parkingId/clients' },
    { breadcrumb: 'userParkings.parkingDetails', element: <ParkingDetailsScreen />, path: '/parking/:parkingId/clients' },

    { breadcrumb: '$clientName', element: <ParkingCompanyScreen />, path: '/parkings/:parkingId/clients/:clientId' },
    { breadcrumb: '$clientName', element: <ParkingCompanyScreen />, path: '/parking/:parkingId/clients/:clientId' },

    { breadcrumb: 'subscriptions.editClient', element: <EnterpriseProfileScreen action='edit' />, path: '/parkings/:parkingId/clients/:clientId/edit' },
    { breadcrumb: 'subscriptions.editClient', element: <EnterpriseProfileScreen action='edit' />, path: '/parking/:parkingId/clients/:clientId/edit' },

    { breadcrumb: 'subscriptions.createCompany', element: <EnterpriseProfileScreen action='add' />, path: '/parkings/:parkingId/clients/add' },
    { breadcrumb: 'subscriptions.createCompany', element: <EnterpriseProfileScreen action='add' />, path: '/parking/:parkingId/clients/add' },

    { breadcrumb: 'subscriptions.clientManagement', element: <ParkingCompanyScreen />, path: '/parkings/:parkingId/clients/:clientId/admins' },
    { breadcrumb: 'subscriptions.clientManagement', element: <ParkingCompanyScreen />, path: '/parking/:parkingId/clients/:clientId/admins' },

    { breadcrumb: 'userGroups.addMember', element: <MemberPropertiesScreen targetType='userGroupAdmin' action='add' />, path: '/parkings/:parkingId/clients/:clientId/admins/add' },
    { breadcrumb: 'userGroups.addMember', element: <MemberPropertiesScreen targetType='userGroupAdmin' action='add' />, path: '/parking/:parkingId/clients/:clientId/admins/add' },

    { breadcrumb: 'userGroups.memberProperties', element: <MemberPropertiesScreen action='edit' targetType='userGroupAdmin' />, path: '/parkings/:parkingId/clients/:clientId/admins/:memberId' },
    { breadcrumb: 'userGroups.memberProperties', element: <MemberPropertiesScreen action='edit' targetType='userGroupAdmin' />, path: '/parking/:parkingId/clients/:clientId/admins/:memberId' },

    { breadcrumb: 'pricing.pricing', element: <PricingEndUserScreen />, path: '/parkings/:parkingId/clients/:clientId/pricing-user' }, // Replace with resaClassId ?
    { breadcrumb: 'pricing.pricing', element: <PricingEndUserScreen />, path: '/parking/:parkingId/clients/:clientId/pricing-user' }, // Replace with resaClassId ?

    { breadcrumb: 'pricing.pricing', element: <PricingClientScreen />, path: '/parkings/:parkingId/clients/:clientId/pricing' }, // Replace with resaClassId ?
    { breadcrumb: 'pricing.pricing', element: <PricingClientScreen />, path: '/parking/:parkingId/clients/:clientId/pricing' }, // Replace with resaClassId ?

    { breadcrumb: 'userParkings.parkingDetails', element: <ParkingDetailsScreen />, path: '/parkings/:parkingId/admins' },
    { breadcrumb: 'userParkings.parkingDetails', element: <ParkingDetailsScreen />, path: '/parking/:parkingId/admins' },

    { breadcrumb: '$parkingName', element: <MemberPropertiesScreen targetType='parkingAdminGroup' action='add' />, path: '/parkings/:parkingId/admins/add' },
    { breadcrumb: '$parkingName', element: <MemberPropertiesScreen targetType='parkingAdminGroup' action='add' />, path: '/parking/:parkingId/admins/add' },

    { breadcrumb: 'userGroups.memberProperties', element: <MemberPropertiesScreen action='edit' targetType='parkingAdminGroup' />, path: '/parkings/:parkingId/admins/:memberId' },
    { breadcrumb: 'userGroups.memberProperties', element: <MemberPropertiesScreen action='edit' targetType='parkingAdminGroup' />, path: '/parking/:parkingId/admins/:memberId' },

    // Subscriptions : Pricing
    { breadcrumb: 'pricing.pricing', element: <PricingEndUserScreen />, path: '/subscriptions/clients/:clientId/pricing-user' }, // Replace with resaClassId ?
    { breadcrumb: '$parkingId.clientName', element: <PricingClientScreen />, path: '/subscriptions/clients/:clientId/pricing' }, // Replace with resaClassId ?

    // Subscriptions : Clients
    { breadcrumb: 'subscriptions.createCompany', element: <EnterpriseProfileScreen action='add' />, path: '/subscriptions/add' },
    { breadcrumb: 'subscriptions.editClient', element: <EnterpriseProfileScreen action='edit' />, path: '/subscriptions/:clientId/edit' },
    { breadcrumb: 'subscriptions.clientManagement', element: <CompaniesScreen />, path: '/subscriptions' },
    { breadcrumb: '$clientName', element: <ParkingCompanyScreen />, path: '/subscriptions/:clientId' }, // TODO find a better name

    // Holding Management
    { breadcrumb: '$holdingName', element: <HoldingDetailsScreen />, path: '/holdings/:holdingId' },
    { breadcrumb: '$holdingName', element: <HoldingPropertiesScreen action='edit' />, path: '/holdings/:holdingId/edit' },
    { breadcrumb: 'userGroups.holding.newHolding', element: <HoldingPropertiesScreen action='add' />, path: '/holdings/add' },
    { breadcrumb: 'userGroups.holding.holdingManagement', element: <HoldingsScreen />, path: '/holdings' },
    { breadcrumb: '$holdingName.addAdmin', element: <MemberPropertiesScreen action='add' targetType='holding' />, path: '/holdings/:holdingId/admins/add' },
    { breadcrumb: '$holdingName.addParking', element: <ParkingPropertiesScreen action='add' targetType='holding' />, path: '/holdings/:holdingId/parkings/add' },
    { breadcrumb: '$holdingName.assignParking', element: <ParkingMemberScreen action='add' targetType='holding' />, path: '/holdings/:holdingId/parkings/assign' },

    // Redirect to / if no route is found
    { breadcrumb: '', element: <Navigate to="/" replace />, path: '*' },
    { breadcrumb: '', element: <PolicyScreen action='view' />, path: '/read-policy' },
    { breadcrumb: '', element: <PolicyScreen />, path: '/policy' },
    { breadcrumb: 'breadcrumb.dashboard', element: <DashboardScreen />, path: '/' },
    { breadcrumb: 'invite', element: <GroupInviteScreen />, path: '/invite' },
    { breadcrumb: 'subscriptions.client', element: <ParkingCompanyScreen />, path: '/company' }, // TODO find a better name
    { breadcrumb: 'parking.parking', element: <ParkingDetailsScreen />, path: '/parking' },

    // User Profile
    { breadcrumb: 'dashboard.userProfile', element: <UserProfileScreen />, path: '/profile' },
    { breadcrumb: 'dashboard.configuration', element: <MFAConfigScreen />, path: 'profile/configure2FA' },
    { breadcrumb: '', element: <ProfileSelectionScreen />, path: '/select-profile' },

    // Planning
    { breadcrumb: 'planning.planning', element: <PlanningScreen />, path: '/groups/:groupId/planning' },

];

export const publicRoutes: { path: string, breadcrumb: string, element: JSX.Element }[] = [
    { breadcrumb: '', element: <AuthScreen />, path: '/login' },
    { breadcrumb: 'forgottenPassword', element: <ForgottenPasswordScreen />, path: '/forgotten-password' },
    { breadcrumb: '', element: <AuthAction />, path: '/auth/action' },
    { breadcrumb: 'invite', element: <AuthScreen isInvite={true} />, path: '/invite' },
    { breadcrumb: '', element: <Navigate to="/login" replace />, path: '*' }
];

const currentBreadcrumbsParamsMap: { [key: string]: string } = {};

const BreadCrumbFragment = (props => {

    const params = Object.keys(props.match.params).map(item => currentBreadcrumbsParamsMap[item]);
    return <span>{params.pop()}</span>;
}) as BreadcrumbComponentType;

export const useBreadcrumbsRoutes = (props: BreadcrumbsProps, t: (key: string) => string) => {
    const breadcrumbsRouteArray: { path: string; breadcrumb: string | BreadcrumbComponentType; element: React.JSX.Element; }[] = [];

    // Parkings that don't ask for a pin code list plain accesses, so the '/my-access' breadcrumb
    // must not name one either — mirrors the conditional title in MyAccessScreen.
    const pinCodeRequired = useIsPinCodeRequired();

    if (props.textKey) currentBreadcrumbsParamsMap[props.textKey as string] = props.name ?? '';
    if (props.keyMap) {
        for (const ky in props.keyMap) {
            currentBreadcrumbsParamsMap[ky] = props.keyMap[ky];
        }
    }


    routes.forEach(route => {
        let { breadcrumb } = route;
        if ((route.breadcrumb as string).startsWith('$')) {
            /*
             *const breadcrumbPropName: string = route.breadcrumb.substring(1);
             *if (breadcrumbPropName in props) {
             *  breadcrumb = props[breadcrumbPropName] as string;
             *}
             */
            breadcrumb = BreadCrumbFragment;
        } else if (route.path === '/my-access') {
            breadcrumb = t(pinCodeRequired ? 'access.allMyPinCode' : 'access.myAccess');
        } else {
            breadcrumb = t((route.breadcrumb as string));
        }

        const breadcrumbsRoute = {
            breadcrumb,
            element: route.element,
            path: route.path
        };

        breadcrumbsRouteArray.push(breadcrumbsRoute);
    });


    return breadcrumbsRouteArray;
};
